import {IReg} from "/@lib@/commons/registry.js";
import {JSX} from "/@lib@/commons/xml/dom.js";
import {IWspEnv, IWspUiEnv, JWspUriChangeMsg, WSP, Wsp} from "/@lib@/wsp/wsp.js";
import {IAction} from "/@lib@/commons/actions.js";
import {IEndPoint} from "/@lib@/commons/io/io.js";
import {BoxEnumEntry, IBoxEnumEltWedlet, IBoxEnumProvider, WedEnumProvider} from "/@back@/edit/wed/wedlets/box/boxTags.js";
import {IItemUiEnv} from "/@back@/wsp/views/itemMain.js";
import {EWspChangesEvts, WspsLivePlace} from "/@lib@/wsp/wspsLive.js";
import {EventMgr} from "/@lib@/commons/events.js";
import {WedMgr} from "/@back@/edit/wed/wedEditor.js";
import {SRC} from "/@lib@/wsp/src.js";
import {WedAutoCompProviderFromEnum} from "/@back@/edit/wed/wedlets/box/boxAutoComplete.js";
import {getXsltProcessor} from "/@back@/wsp/widgets/wed/enums/xsltCache.js";

/**
 * Appelé au chargement dans le WED :
 * <jslib src=":back:wsp/widgets/wed/enums/enumPrvFromItem.js"/>
 * Déclare un svc 'wsp-wed-enumprovider-fromitem'
 * qui doit être déclaré dans le widget Wed. Exemple :
 * <box-input-enum enumSvc="wsp-wed-enumprovider-fromitem" itemSrcUri="" publicWspCode?="" transform?="" xslUrl?=""/>
 */
export async function jslibAsyncInit(jsEndPoint: IEndPoint, elt: Element, reg: IReg<any>) {
	reg.registerSvc('wsp-wed-enumprovider-fromitem', 1, (tpl: Element, wedMgr: WedMgr) => {
		const env = wedMgr.reg.env as IWspEnv;
		let wsp: Wsp;
		if (tpl.hasAttribute("publicWspCode")) {
			const wspCd = tpl.getAttribute("publicWspCode");
			wsp = env.universe.wspServer.wspsLive.findWsp(wspCd, true);
		} else {
			wsp = env.wsp;
		}
		const wspCache = wsp.reg.cache; //Cache sur le registre de l'atelier détenteur de cette énum.
		let enumPrv = wspCache.get(tpl) as EnumPrvFromItem;
		if (!enumPrv) {
			enumPrv = new EnumPrvFromItem(tpl, wedMgr, wsp);
			wspCache.set(tpl, enumPrv);
		}
		//Gestion du kill de l'enum
		if (!enumPrv.usedBy.has(wedMgr)) {
			enumPrv.usedBy.add(wedMgr);
			wedMgr.listeners.on("killEditor", () => {
				enumPrv.usedBy.delete(wedMgr);
				if (enumPrv.usedBy.size === 0) {
					enumPrv.killEnum();
					wspCache.delete(tpl);
				}
			})
		}
		return enumPrv;
	});

	reg.registerSvc('wsp-wed-autoCompetePrv-fromitem', 1, (tpl: Element, wedMgr: WedMgr) => {
		const cache = wedMgr.getDatasForModel(tpl);
		let enumPrv: WedAutoCompProviderFromEnum = cache['wsp-wed-autoCompetePrv-fromitem'];
		if (!enumPrv) {
			enumPrv = new WedAutoCompProviderFromEnum('wsp-wed-enumprovider-fromitem', wedMgr, tpl);
			cache['wsp-wed-autoCompetePrv-fromitem'] = enumPrv;
		}
		return enumPrv;
	});
}

/**
 * Fournisseur d'enum issu d'un item
 * <xxx itemSrcUri="/monItem" publicWspCode?="wsp courant par défaut" transform?="" xslUrl?="" />
 */
export class EnumPrvFromItem extends WedEnumProvider implements IBoxEnumProvider {
	onEnumChange = new EventMgr<() => void>();


	usedBy: Set<WedMgr> = new Set();

	protected initing: Promise<void>
	protected fetching: Promise<void>
	protected fetchCounter = 0;
	protected reg: IReg<IWspUiEnv & IItemUiEnv>
	protected wsp: Wsp | null;
	protected itemSrcUri: string;
	protected transform?: string;
	protected place: WspsLivePlace;
	/** Si null chargement de la xsl en erreur*/
	protected xslt?: XSLTProcessor | null;

	constructor(tpl: Element, wedMgr: WedMgr, wsp: Wsp) {
		super(null);
		this.reg = wedMgr.reg;
		this.wsp = wsp;
		this.initing = this.init(tpl, wedMgr);
	}

	async init(tpl: Element, wedMgr: WedMgr) {
		try {
			// Xsl
			if (tpl.hasAttribute("xslUrl")) {
				try {
					this.xslt = await getXsltProcessor(this.reg.env.itemType.datas.wspResUrl, tpl);
				} catch (e) {
					console.error(e);
					this.xslt = null;
					return;
				}
			}

			// - Wsp
			if (this.wsp !== this.reg.env.wsp) {
				//Atelier public.
				this.wsp.listenChanges();
			}

			// Listener sur les changements de la ressource
			this.place = this.wsp.newPlaceWsp();
			this.place.eventsMgr.on("wspUriChange", (msg: JWspUriChangeMsg, from) => {
				if (msg.wspCd !== this.wsp.code) return;
				if ((msg.type === EWspChangesEvts.r && SRC.isSubUriOrEqual(msg.srcUri, this.itemSrcUri))
					|| (msg.type === EWspChangesEvts.u && this.itemSrcUri === msg.srcUri)) {
					if (this.resetActions()) this.onEnumChange.emit();
				}
			});
			const reset = () => {if (this.resetActions()) this.onEnumChange.emit()}
			this.place.eventsMgr.on("wspLiveStateChange", reset);
			this.place.eventsMgr.on("onConnectionRenewed", reset);

			// Item
			this.itemSrcUri = tpl.getAttribute('itemSrcUri');
			this.transform = tpl.getAttribute('transform');
		} finally {
			this.initing = null; //fin de l'init
		}
	}

	killEnum() {
		this.place.closePlace();
		if (this.wsp !== this.reg.env.wsp) this.wsp.stopListenChanges();
	}

	protected async fetchEnum() {
		if (this.initing) await this.initing;
		if (!this.fetching && !this._actions) this.fetching = this._fetchEnum();
		while (this.fetching) await this.fetching;
	}

	protected async _fetchEnum() {
		const counter = this.fetchCounter;
		if (this.xslt === null) {
			// xsl configurée mais echec à sa construction.
			this._actions = [new BoxEnumEntry(null).setEnabled(false).setLabel("Chargement du composant en erreur")];
			await Promise.resolve();
			this.fetching = null;
			return;
		}
		if (this.wsp !== this.reg.env.wsp) {
			if (!this.wsp.isLoaded) {
				try {
					await this.wsp.waitForLoad();
					if (counter !== this.fetchCounter) return; //raceCond
				} catch (e) {
					this.fetching = null;
					this._actions = [new BoxEnumEntry(null).setEnabled(false).setLabel("L'atelier contenant cette liste est inaccessible")];
					console.error(e);
					return;
				}
			}
			if (!this.wsp.isPublic) {
				this._actions = [new BoxEnumEntry(null).setEnabled(false).setLabel("L'atelier contenant cette liste n'est pas déclaré 'public' (voir les propriétés de l'atelier)")];
				await Promise.resolve();
				this.fetching = null;
				return;
			}
		}
		try {
			let dom = await WSP.fetchStreamDom(this.wsp, this.reg.env.uiRoot, this.itemSrcUri, this.transform);
			if (counter !== this.fetchCounter) return; //raceCond
			if (!dom) throw Error(`Item '${this.itemSrcUri}' not found in wsp '${this.wsp.code}'`);

			if (this.xslt) dom = this.xslt.transformToDocument(dom);

			this._actions = dom ? WedEnumProvider.buildActions(dom.documentElement, this) : [];
			this.fetching = null;
		} catch (e) {
			this.fetching = null;
			this._actions = [new BoxEnumEntry(null).setEnabled(false).setLabel("Construction de l'énumération en erreur")];
			throw e;
		}
	}

	/** Retourne true si un reset a eu lieu */
	protected resetActions(): boolean {
		this.fetchCounter++;
		if (this.fetching) this.fetching = this._fetchEnum(); //chargement en cours annulé, on recommence.
		if (this._actions) {
			this._actions = null;
			return true;
		}
		return false;
	}

	getActions(widget: IBoxEnumEltWedlet, filter?: string): IAction<IBoxEnumEltWedlet>[] | Promise<IAction<IBoxEnumEltWedlet>[]> {
		if (!this._actions) return this.fetchEnum().then(() => super.getActions(widget, filter));
		return super.getActions(widget, filter);
	}

	getAction(key: string, widget: IBoxEnumEltWedlet): IAction<IBoxEnumEltWedlet> | null | Promise<IAction<IBoxEnumEltWedlet> | null> {
		if (!this._actions) return this.fetchEnum().then(() => super.getAction(key, widget));
		return super.getAction(key, widget);
	}
}